import { useFeatureFlag } from '@shared/feature-flags/useFeatureFlag'
import { CalendarClock, History } from 'lucide-react'
import { Card, MoneyValue } from '@shared/design-system/ui'
import { balanceName, bucketKey, nextRelease, nextReleaseDate } from '@entities/wallet'
import type { BalanceSummary } from '@entities/wallet'
import {
  availableBlock,
  balanceGrid,
  bucketCard,
  bucketFooter,
  bucketHeader,
  bucketName,
  bucketTitleBox,
  fieldLabel,
  helpText,
  iconTile,
  metric,
  metricLabel,
  metricValue,
  receiptLine,
  secondaryMetrics,
} from './wallet.css'

export function BalanceCards({ balances }: { balances: BalanceSummary[] }) {
  const receiptsExperience = useFeatureFlag('receiptsExperience')
  return (
    <div className={balanceGrid} data-testid="wallet-balances">
      {balances.map((balance) => {
        const release = nextRelease(balance)
        const releaseDate = nextReleaseDate(balance)
        const metrics = [
          {
            label: receiptsExperience ? 'Disponível' : 'A liberar',
            value: receiptsExperience ? balance.available : balance.pending,
          },
          { label: 'Reservado', value: balance.reserve },
          { label: 'Dívida em aberto', value: balance.openDebt },
          { label: 'Em envio', value: balance.processingWithdrawal },
          { label: 'Em recuperação de dívida', value: balance.processingDebtRecovery },
        ]
        return (
          <Card
            key={bucketKey(balance)}
            padding="md"
            className={bucketCard({ experience: 'ON_REQUEST' })}
          >
            <div className={bucketHeader}>
              <span className={iconTile({ experience: 'ON_REQUEST' })}>
                <History size={19} />
              </span>
              <div className={bucketTitleBox}>
                <span className={bucketName}>{balanceName(balance)}</span>
              </div>
            </div>
            <div className={availableBlock}>
              <span className={fieldLabel}>{receiptsExperience ? 'A liberar' : 'Disponível'}</span>
              <MoneyValue
                {...(receiptsExperience ? balance.pending : balance.available)}
                size="xl"
              />
            </div>
            <div className={secondaryMetrics}>
              {metrics.map(({ label, value }) => (
                <div className={metric} key={label}>
                  <span className={metricLabel}>{label}</span>
                  <span className={metricValue}>
                    <MoneyValue {...value} size="sm" />
                  </span>
                </div>
              ))}
            </div>
            <div className={bucketFooter}>
              {release && releaseDate ? (
                <div className={receiptLine}>
                  <CalendarClock size={16} />
                  Próxima liberação: <MoneyValue {...release.amount} size="sm" /> em {releaseDate}
                </div>
              ) : null}
              {balance.awaitingAvailability && Number(balance.awaitingAvailability.amount) > 0 ? (
                <span className={helpText}>
                  Aguardando previsão de liberação:{' '}
                  <MoneyValue {...balance.awaitingAvailability} size="sm" />
                </span>
              ) : null}
              <span className={helpText}>
                Saldo e liberações da carteira, sem conversão entre moedas. A data de liberação não
                confirma depósito bancário.
              </span>
            </div>
          </Card>
        )
      })}
    </div>
  )
}
